import { ServiceTypes, UserRoles } from 'config/global-enums';

import { createSelector } from '@reduxjs/toolkit';
import { initialState } from '.';

// First select the relevant part from the state
export const selectAuthSliceState = state =>
  state.authenticationForm || initialState;

export const selectAuthentication = createSelector(
  [selectAuthSliceState],
  authFormState => authFormState,
);

export const selectLoading = createSelector(
  [selectAuthSliceState],
  authFormState => authFormState.loading,
);

export const selectAuthType = createSelector(
  [selectAuthSliceState],
  authFormState => authFormState.authType,
);

export const selectFirstRegistration = createSelector(
  [selectAuthSliceState],
  authFormState => authFormState.firstRegistration,
);

export const selectFirstRegCompleted = createSelector(
  [selectAuthSliceState],
  authFormState => authFormState.firstRegCompleted,
);

export const selectUserLoginType = createSelector(
  [selectAuthSliceState],
  authFormState => authFormState.userLoginType,
);

export const selectAuthLoading = createSelector(
  [selectAuthSliceState],
  authFormState => authFormState.authLoading,
);

export const selectError = createSelector(
  [selectAuthSliceState],
  authFormState => authFormState.error,
);

export const selectPhoneError = createSelector(
  [selectAuthSliceState],
  authFormState => authFormState.phoneError,
);

export const selectAuthenticated = createSelector(
  [selectAuthSliceState],
  authFormState => authFormState.isAuthenticated,
);

export const selectRegistrationStatus = createSelector(
  [selectAuthSliceState],
  authFormState => authFormState.isRegistered,
);

export const selectVerificationStatus = createSelector(
  [selectAuthSliceState],
  authFormState => authFormState.isUserVerified,
);

export const selectFormSubmitStatus = createSelector(
  [selectAuthSliceState],
  authFormState => authFormState.formSubmitted,
);

export const selectUserDetails = createSelector(
  [selectAuthSliceState],
  authFormState => {
    return {
      userDetails: authFormState.userDetails,
      userObject: {
        id: authFormState.userDetails?.id,
        type: ServiceTypes.PERSON,
      },
    };
  },
);

export const selectOfficeDetails = createSelector(
  [selectAuthSliceState],
  authFormState => {
    const officeId = authFormState.officeDetails?.id;
    const originalOfficeId = authFormState.originalOfficeId;
    const userRole = authFormState.userDetails?.permissions?.find(
      officeItem => officeItem.id === officeId,
    )?.role_code;

    return {
      officeId,
      originalOfficeId,
      officeDetails: authFormState.officeDetails,
      isUserAdmin: userRole === UserRoles.admin,
      userRole,
    };
  },
);

export const selectDateFormats = createSelector(
  [selectAuthSliceState],
  authFormState => {
    const dateFormat =
      authFormState.userDetails?.organisation_settings?.date_format ||
      'dd-MMM-yyyy';

    const timeFormat =
      authFormState.userDetails?.organisation_settings?.time_format === '12'
        ? 'h:mm a'
        : 'H:mm';

    // Function to convert date format to Ant Design format using regex
    const convertToAntdFormat = format => {
      return format.replace(/[a-z]/g, letter => letter.toUpperCase());
    };

    const antdDateFormat = convertToAntdFormat(dateFormat);

    return {
      dateFormat,
      timeFormat,
      antdDateFormat,
    };
  },
);

export const selectJobsDetails = createSelector(
  [selectAuthSliceState],
  authFormState => {
    const jobsData = authFormState.data; // Access the data from the API response

    return {
      totalJobListings: jobsData?.total_job_listings,
      activeJobListings: jobsData?.active_job_listings,
      closedJobListings: jobsData?.closed_job_listings,
      totalApplicationsReceived: jobsData?.total_applications_received,
      scheduledInterviews: jobsData?.scheduled_interview,
      newApplications: jobsData?.new_applications,
      stagewisePercentage: jobsData?.stagewise_percentage,
    };
  },
);

export const selectDemoOrgDetails = createSelector(
  [selectAuthSliceState],
  authFormState => authFormState.demoOrgDetails,
);

export const selectRegPrefilledValues = createSelector(
  [selectAuthSliceState],
  authFormState => authFormState.regPrefilledValues,
);

export const selectUserType = createSelector(
  [selectAuthSliceState],
  authFormState => authFormState.userType,
);

export const selectHideForOzone = createSelector(
  [selectAuthSliceState],
  authFormState => authFormState.hideForOzone,
);

export const selectChatWidgetQueryParams = createSelector(
  [selectAuthSliceState],
  authFormState => authFormState.chatWidgetQueryParams,
);
